import { Box, Column, Row, Typography } from '@tempmee/react';
import {
  OutlineMinusIcon,
  OutlinePlusIcon,
  SolidCheckCircleIcon,
  SolidExclamationWarningAlertIcon,
} from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { forwardRef, memo, type Ref } from 'react';
import { useAverageRate, useRateFloor } from '~/domain/Jobs';
import { UserProfession } from '~/enums/UserProfession';
import { IconButton } from '../../../common/IconButton';
import { useDefaultRecommendedRate } from '../hooks/useDefaultRecommendedRate';
import { getRateLikelihood, type TRateLikelihoodTone } from './rateLikelihood';

interface IRateSelectionProps {
  value?: number | null;
  onChange?: (value: number) => void;
  professionType: string;
  officeId: string;
  hasError?: boolean;
}

const RATE_STEP = 1;
const SUBTEXT = 'Rate based on regional data and time to shift.';

const toneColor = (tone: TRateLikelihoodTone): string => {
  if (tone === 'positive') return colors.success[500];
  if (tone === 'caution') return colors.warning[500];
  return colors.error[500];
};

const toneIcon = (tone: TRateLikelihoodTone) => {
  if (tone === 'positive') {
    return <SolidCheckCircleIcon size={20} color={colors.success[500]} />;
  }
  const color = tone === 'caution' ? colors.warning[500] : colors.error[500];
  return <SolidExclamationWarningAlertIcon size={20} color={color} />;
};

const StepperButton = ({
  direction,
  disabled,
  onPress,
}: {
  direction: 'decrement' | 'increment';
  disabled: boolean;
  onPress: () => void;
}) => (
  <IconButton
    ariaLabel={direction === 'decrement' ? 'Decrease rate' : 'Increase rate'}
    dataTestId={`rate-${direction}`}
    dataCy={`rate-${direction}`}
    disabled={disabled}
    onClick={onPress}
    Icon={
      direction === 'decrement' ? (
        <OutlineMinusIcon size={20} color={colors.secondary[800]} />
      ) : (
        <OutlinePlusIcon size={20} color={colors.secondary[800]} />
      )
    }
  />
);

const Component = (
  { value, onChange, professionType, officeId }: IRateSelectionProps,
  ref: Ref<HTMLDivElement>,
) => {
  const { data: averageRating } = useAverageRate(officeId);

  const professionTypeCode = UserProfession.getCode(professionType) as string | undefined;
  const recommendation = professionTypeCode ? averageRating?.[professionTypeCode] : undefined;
  const profRate = recommendation || { average: 49, min: 18, max: 100 };
  const averageRate = Math.round(profRate.average);

  const { effectiveMin } = useRateFloor({
    professionCode: professionTypeCode,
    averageRate: profRate.average,
    apiMin: profRate.min,
  });

  // Default the rate to the office's recommended (average) rate, matching the
  // legacy field. Only when we have a real recommendation.
  useDefaultRecommendedRate({
    value,
    officeId,
    recommendedRate: recommendation ? averageRate : null,
    onChange,
  });

  const hasValue = value != null;
  const canDecrement = Boolean(officeId) && hasValue && value > effectiveMin;
  const canIncrement = Boolean(officeId) && hasValue && value < profRate.max;

  const handleDecrement = () => {
    onChange?.(Math.max(effectiveMin, (value ?? effectiveMin) - RATE_STEP));
  };

  const handleIncrement = () => {
    onChange?.(Math.min(profRate.max, (value ?? averageRate) + RATE_STEP));
  };

  const likelihood = hasValue ? getRateLikelihood(value, averageRate) : null;

  return (
    <Box ref={ref} data-testid="rate-selection" data-cy="rate-selection" pt={8}>
      {/* Section header — same format as the "Hours" section. */}
      <Typography as="span" bold size="heading_3" color={colors.neutral[600]}>
        Professional Pay
      </Typography>
      <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100], marginTop: 12 }} />

      {/* Centered stepper: buttons flank the rate. */}
      <Row align="center" gap={5} style={{ justifyContent: 'center', marginTop: 24 }}>
        <StepperButton direction="decrement" disabled={!canDecrement} onPress={handleDecrement} />
        <Box
          data-testid="rate-value"
          data-cy="rate-value"
          style={{ minWidth: 72, textAlign: 'center' }}
        >
          {hasValue ? (
            <>
              <Typography
                as="span"
                color={colors.neutral[600]}
                size="heading_4"
                style={{ verticalAlign: '16px' }}
              >
                $
              </Typography>
              <Typography as="span" color={colors.neutral[600]} style={{ fontSize: 40 }}>
                {value}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                as="span"
                color={colors.neutral[400]}
                size="heading_4"
                style={{ verticalAlign: '16px' }}
              >
                $
              </Typography>
              <Typography as="span" color={colors.neutral[400]} style={{ fontSize: 40 }}>
                --
              </Typography>
            </>
          )}
        </Box>
        <StepperButton direction="increment" disabled={!canIncrement} onPress={handleIncrement} />
      </Row>

      {likelihood && (
        <Column align="center" gap={1} style={{ marginTop: 12 }}>
          <Row align="center" gap={1} data-testid="rate-likelihood" data-cy="rate-likelihood">
            <Box
              as="span"
              role="img"
              aria-label="rate-likelihood"
              style={{ display: 'flex', flexShrink: 0 }}
            >
              {toneIcon(likelihood.tone)}
            </Box>
            <Typography as="span" bold size="body_large" color={toneColor(likelihood.tone)}>
              {likelihood.label}
            </Typography>
          </Row>
          <Typography
            color={colors.neutral[800]}
            italic
            size="caption"
            style={{ textAlign: 'center' }}
          >
            {SUBTEXT}
          </Typography>
        </Column>
      )}
    </Box>
  );
};

export const RateSelection = memo(forwardRef(Component));
